import React, {useId} from 'react';
import {Radio, Tooltip, theme} from 'antd';
import {css, Global as GlobalStyles} from '@emotion/react';
import TableOutlined from '@ant-design/icons/lib/icons/TableOutlined';
import UnorderedListOutlined from '@ant-design/icons/lib/icons/UnorderedListOutlined';
import GlobalOutlined from '@ant-design/icons/lib/icons/GlobalOutlined';
import PicCenterOutlined from '@ant-design/icons/lib/icons/PicCenterOutlined';
import {isBrowser} from 'device-detect';
import {useTranslation} from 'react-i18next';

function MasonryIcon() {
    return <svg viewBox="0 0 20 20" width="1em" height="1em" fill="none" stroke="currentColor"
                strokeWidth="1.4" aria-hidden="true" focusable="false">
        <rect x="2" y="2" width="6.5" height="9" rx="1"/>
        <rect x="2" y="14" width="6.5" height="4" rx="1"/>
        <rect x="11.5" y="2" width="6.5" height="4" rx="1"/>
        <rect x="11.5" y="9" width="6.5" height="9" rx="1"/>
    </svg>;
}

const modes = [
    {value: 'grid', label: 'Grid', Icon: TableOutlined, shortcut: 'g', tooltip: 'Grid View'},
    {value: 'masonry', label: 'Masonry', Icon: MasonryIcon, shortcut: 'y', tooltip: 'Masonry View'},
    {value: 'table', label: 'Table', Icon: UnorderedListOutlined, shortcut: 't', tooltip: 'Table View'},
    {value: 'map', label: 'Map', Icon: GlobalOutlined, shortcut: 'm', tooltip: 'Map View'},
    {value: 'posts', label: 'Posts', Icon: PicCenterOutlined, shortcut: 'p', tooltip: 'Posts View'},
];

export default function ViewModeSelector({value, onChange}) {
    const {t} = useTranslation();
    const {token} = theme.useToken();
    const id = useId().replace(/:/g, '');
    const labelId = `view-mode-label-${id}`;

    return <div>
        <strong id={labelId}>{t('view-mode', 'View Mode')}:</strong>
        <GlobalStyles styles={css`
            #view-mode-radios {
                display: grid;
                grid-template-columns: repeat(6, minmax(0, 1fr));
                gap: 4px;
                margin-top: 4px;
                width: 100%;
                max-width: 200px;
                margin-inline: auto;
            }
            #view-mode-radios .view-mode-tile {
                grid-column: span 2;
                display: flex;
                align-items: center;
                justify-content: center;
                min-width: 0;
                min-height: 36px;
                height: auto;
                padding: 2px 4px;
                font-size: 12px;
                line-height: 1.25;
                white-space: normal;
                text-align: center;
                border: 1px solid ${token.colorBorder};
                border-radius: ${token.borderRadiusSM}px;
                background: ${token.colorBgContainer};
                color: ${token.colorText};
            }
            #view-mode-radios .view-mode-tile::before { display: none; }
            #view-mode-radios .view-mode-tile-map { grid-column: 2 / span 2; }
            #view-mode-radios .view-mode-tile-posts { grid-column: 4 / span 2; }
            #view-mode-radios .view-mode-tile:hover {
                border-color: ${token.colorPrimaryBorderHover};
                background: ${token.colorFillTertiary};
            }
            #view-mode-radios .view-mode-tile.ant-radio-button-wrapper-checked {
                border-color: ${token.colorPrimary};
                background: ${token.colorPrimaryBg};
                color: ${token.colorPrimaryText};
            }
            #view-mode-radios .view-mode-tile:has(input:focus-visible) {
                outline: 2px solid ${token.colorPrimary};
                outline-offset: 2px;
            }
            #view-mode-radios .view-mode-tile-content {
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 1px;
                overflow-wrap: anywhere;
            }
            #view-mode-radios .view-mode-tile-content > .anticon,
            #view-mode-radios .view-mode-tile-content > svg {
                width: 12px;
                height: 12px;
                font-size: 12px;
            }
        `}/>
        <Radio.Group role="radiogroup" id="view-mode-radios" name={`view-mode-${id}`} aria-labelledby={labelId}
                     value={value} onChange={onChange}>
            {modes.map(({value, label, Icon, shortcut, tooltip}) =>
                <Tooltip key={value} title={shortcut && isBrowser() ? `${t(`tooltip-${value}-view`, tooltip)}: ${shortcut}` : undefined}>
                    <Radio.Button value={value} id={`${value}-view-btn`} className={`view-mode-tile view-mode-tile-${value}`}>
                        <span className="view-mode-tile-content">
                            <Icon aria-hidden="true"/>
                            <span>{t(value, label)}</span>
                        </span>
                    </Radio.Button>
                </Tooltip>
            )}
        </Radio.Group>
    </div>;
}
